import { Button } from '@app/components/various/Button';
import { PageTitle } from '@app/components/various/PageTitle';
import { Skeleton } from '@app/components/various/Skeleton';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { compiledPaths } from '@app/paths';
import { useAccountDetails } from '@app/services/hooks/account/useAccountDetails';
import { useShippingAddresses } from '@app/services/hooks/account/useShippingAddresses';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { BuyersList } from '../BuyersList';

import { AccountInformation } from './AccountInformation';
import { InvoicingAddressForm } from './InvoicingAddressForm';
import { ShippingAddresses } from './ShippingAddresses';

export const AccountDetails = () => {
  const { t } = useTranslation(['common', 'accounts', 'validation', 'buyers']);
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);

  const { data: accountDetails, isLoading: isLoadingAccount, isError } = useAccountDetails({ accountId });
  const { data: shippingAddresses, isLoading: isLoadingShippingAddresses } = useShippingAddresses({ accountId });

  React.useEffect(() => {
    if (isError) {
      navigate(compiledPaths.ACCOUNTS({}));
    }
  }, [isError, navigate]);

  const goBackToAccounts = () => {
    navigate(compiledPaths.ACCOUNTS({}));
  };

  if (!isDefined(accountId)) {
    return null;
  }

  const accountName = accountDetails?.name ?? '';

  return (
    <PageTitle title={`${t('accounts:account_other')} - ${accountName}`}>
      <div className="mx-auto w-full max-w-screen-xl px-6 pb-16 pt-3">
        <div className="flex items-center justify-between gap-3">
          <SkeletonLayout isLoading={isLoadingAccount} skeleton={<Skeleton type="text" size="xlarge" width={isMobile ? 60 : 30} />}>
            <h2 data-sentry-mask className="heading-1">
              {accountName}
            </h2>
          </SkeletonLayout>
          <Button variant="bordered" color="secondary" className="shrink-0" onClick={goBackToAccounts}>
            {isMobile ? t('common:back') : t('accounts:back_to_accounts')}
          </Button>
        </div>

        <section className="box mt-4">
          <AccountInformation
            name={accountName}
            email={accountDetails?.address.email ?? ''}
            market={accountDetails?.market}
            pricelist={accountDetails?.pricelist}
            isLoading={isLoadingAccount}
          />
        </section>

        <section className="box mt-4">
          <BuyersList accountId={accountId} buyers={Object.values(accountDetails?.buyers ?? {})} isLoading={isLoadingAccount} />
        </section>

        <section className="box mt-4">
          <ShippingAddresses accountId={accountId} shippingAddresses={shippingAddresses} isLoading={isLoadingShippingAddresses} />
        </section>

        {isDefined(accountDetails) && (
          <section className="box mt-4">
            <InvoicingAddressForm accountId={accountId} address={accountDetails.billingAddress} />
          </section>
        )}
      </div>
    </PageTitle>
  );
};
